export interface FlatStepConfig {
  id: string;
  title: string;
  path: string;
  group: string;
  groupTitle: string;
  /** If true, navigating away from this step will replace the history entry instead of pushing */
  skipHistory?: boolean;
}

export const FLAT_STEP_CONFIGS: FlatStepConfig[] = [
  {
    id: 'assets',
    title: 'Select Assets',
    path: 'assets',
    group: 'assets',
    groupTitle: 'Select Assets',
  },
  {
    id: 'assessment-intro',
    title: 'Introduction',
    path: 'assessment-intro',
    group: 'assessment',
    groupTitle: 'Coverage Assessment',
  },
  {
    id: 'assessment-accounts',
    title: 'Account Details',
    path: 'assessment-accounts',
    group: 'assessment',
    groupTitle: 'Coverage Assessment',
  },
  {
    id: 'assessment-projection',
    title: 'Projection',
    path: 'assessment-projection',
    group: 'assessment',
    groupTitle: 'Coverage Assessment',
  },
  {
    id: 'preferences-death-benefits',
    title: 'Death Benefits',
    path: 'preferences-death-benefits',
    group: 'preferences',
    groupTitle: 'Policy Preferences',
  },
  {
    id: 'preferences-payment',
    title: 'Payment Preference',
    path: 'preferences-payment',
    group: 'preferences',
    groupTitle: 'Policy Preferences',
  },
  {
    id: 'preferences-benefit-structure',
    title: 'Benefit Structure',
    path: 'preferences-benefit-structure',
    group: 'preferences',
    groupTitle: 'Policy Preferences',
  },
  {
    id: 'options',
    title: 'Policy Types',
    path: 'options',
    group: 'preferences',
    groupTitle: 'Policy Preferences',
  },
  {
    id: 'quotes-loading',
    title: 'Finding Policies',
    path: 'quotes-loading',
    group: 'preferences',
    groupTitle: 'Policy Preferences',
    skipHistory: true,
  },
  {
    id: 'quotes-selection',
    title: 'Policy Options',
    path: 'quotes-selection',
    group: 'quotes',
    groupTitle: 'Policy Options',
  },
  {
    id: 'quotes-funding',
    title: 'Policy Funding',
    path: 'quotes-funding',
    group: 'quotes',
    groupTitle: 'Policy Options',
  },
  {
    id: 'plan-comparison',
    title: 'Plan Comparison',
    path: 'plan-comparison',
    group: 'plan',
    groupTitle: 'Plan',
  },
];

export const STEP_GROUPS = [...new Set(FLAT_STEP_CONFIGS.map(s => s.group))];

export function getStepIndex(stepId: string): number {
  const index = FLAT_STEP_CONFIGS.findIndex(s => s.id === stepId);
  if (index === -1) {
    throw new Error(`Step not found: ${stepId}`);
  }
  return index;
}

export function getStepId(stepIndex: number): GuidedPlanningStep {
  const stepConfig = FLAT_STEP_CONFIGS[stepIndex];
  if (!stepConfig) {
    throw new Error(`Invalid step index: ${stepIndex}`);
  }
  return stepConfig.id as GuidedPlanningStep;
}

export const ASSET_TYPES = [
  { id: 'roth-ira', label: 'Roth IRA' },
  { id: 'roth-401k', label: 'Roth 401(k)' },
  { id: 'traditional-ira', label: 'Traditional IRA' },
  { id: 'traditional-401k', label: 'Traditional 401(k)' },
  { id: 'hsa', label: 'HSA Account' },
  { id: 'brokerage', label: 'Brokerage Account' },
  { id: 'home-equity', label: 'Home Equity' },
  { id: 'retirement-income', label: 'Retirement Income' },
  { id: 'cryptocurrency', label: 'Cryptocurrency' },
] as const;

export type AssetTypeId = (typeof ASSET_TYPES)[number]['id'];

/**
 * Maps guided planning asset IDs (kebab-case) to the Redux/backend accountType format (camelCase).
 */
export const ASSET_TYPE_TO_ACCOUNT_TYPE: Record<AssetTypeId, string> = {
  'roth-ira': 'rothIra',
  'roth-401k': 'roth401k',
  'traditional-ira': 'traditionalIra',
  'traditional-401k': 'traditional401k',
  'hsa': 'hsa',
  'brokerage': 'brokerage',
  'home-equity': 'homeEquity',
  'retirement-income': 'retirementIncome',
  'cryptocurrency': 'cryptocurrency',
};
